import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Production WebSocket URL - handles both http/https
const getWebSocketUrl = () => {
  const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
  // Convert http(s) to ws(s)
  return baseUrl.replace('https://', 'wss://').replace('http://', 'ws://');
};
const WS_URL = getWebSocketUrl();

const apiCall = async (method, url, data = null) => {
  const token = localStorage.getItem('token');
  const config = {
    method,
    url: `${API}${url}`,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  };
  if (data) config.data = data;
  return axios(config);
};

// Notification Bell Component for Header with WebSocket support
export const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);

  // Load notifications via REST API
  const loadNotifications = useCallback(async () => {
    try {
      const response = await apiCall('get', '/notifications');
      setNotifications(response.data?.notifications || []);
      setUnreadCount(response.data?.unread_count || 0);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    }
  }, []);

  // Connect to WebSocket for real-time notifications
  const connectWebSocket = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token || !WS_URL) return;

    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }

    try {
      const ws = new WebSocket(`${WS_URL}/ws/notifications/${token}`);
      
      ws.onopen = () => {
        console.log('WebSocket connected');
        setWsConnected(true);
        reconnectAttempts.current = 0;  // Reset on successful connect
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          
          if (message.type === 'connected') {
            setUnreadCount(message.data?.unread_count || 0);
          } else if (message.type === 'notification') {
            // Add new notification to the top of the list
            setNotifications(prev => [message.data, ...prev.slice(0, 49)]);
            setUnreadCount(prev => prev + 1);
            
            // Play notification sound (optional)
            // const audio = new Audio('/notification.mp3');
            // audio.play().catch(() => {});
          }
        } catch (e) {
          // Handle ping/pong
          if (event.data === 'ping') {
            ws.send('pong');
          }
        }
      };

      ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        setWsConnected(false);
        wsRef.current = null;
        
        // Reconnect with exponential backoff if not a normal close
        // Codes 4001-4003 are auth errors, don't reconnect
        if (event.code !== 1000 && event.code < 4001) {
          const delay = Math.min(30000, 5000 * Math.pow(1.5, reconnectAttempts.current || 0));
          console.log(`WebSocket reconnecting in ${delay}ms...`);
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttempts.current = (reconnectAttempts.current || 0) + 1;
            connectWebSocket();
          }, delay);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setWsConnected(false);
      };

      wsRef.current = ws;
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
    }
  }, []);

  // Initialize: load notifications and connect WebSocket
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    loadNotifications();
    connectWebSocket();

    // Fallback polling every 60 seconds if WebSocket is not connected
    const pollInterval = setInterval(() => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        loadNotifications();
      }
    }, 60000);

    return () => {
      clearInterval(pollInterval);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close(1000);
      }
    };
  }, [loadNotifications, connectWebSocket]);

  const markAsRead = async (notificationId) => {
    try {
      await apiCall('post', `/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await apiCall('post', '/notifications/mark-all-read');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'new_chapter': return '📖';
      case 'comment': return '💬';
      case 'vote': return '❤️';
      case 'achievement': return '🏆';
      case 'contest': return '🎯';
      case 'payment': return '💰';
      default: return '🔔';
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative p-2 text-dark-text-secondary hover:text-dark-accent transition-colors"
        data-testid="notification-bell"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
        {/* WebSocket connection indicator */}
        <span className={`absolute bottom-0 right-0 w-2 h-2 rounded-full ${wsConnected ? 'bg-green-500' : 'bg-gray-500'}`} title={wsConnected ? 'Real-time connected' : 'Polling mode'} />
      </button>

      {showDropdown && (
        <div 
          className="absolute right-0 mt-2 w-80 bg-dark-card rounded-xl shadow-xl border border-dark-text-secondary/20 z-50"
          data-testid="notification-dropdown"
        >
          <div className="p-4 border-b border-dark-text-secondary/20 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-dark-text">Notifications</h3>
              {wsConnected && (
                <span className="text-[10px] text-green-400 bg-green-500/20 px-2 py-0.5 rounded-full">Live</span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-xs text-purple-400 hover:text-purple-300"
              >
                Mark all read
              </button>
            )}
          </div>
          
          <div className="max-h-96 overflow-y-auto">
            {notifications.length > 0 ? (
              notifications.slice(0, 10).map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => !notification.is_read && markAsRead(notification.id)}
                  className={`p-4 border-b border-dark-text-secondary/10 hover:bg-dark-bg/50 cursor-pointer transition-colors ${
                    !notification.is_read ? 'bg-purple-500/5' : ''
                  }`}
                  data-testid={`notification-${notification.id}`}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">{getNotificationIcon(notification.type)}</span>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${notification.is_read ? 'text-dark-text-secondary' : 'text-dark-text'}`}>
                        {notification.message}
                      </p>
                      <p className="text-xs text-dark-text-secondary mt-1">
                        {new Date(notification.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    {!notification.is_read && (
                      <span className="w-2 h-2 bg-purple-500 rounded-full flex-shrink-0 mt-2" />
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="p-8 text-center text-dark-text-secondary">
                <p className="text-3xl mb-2">🔔</p>
                <p>No notifications yet</p>
              </div>
            )}
          </div>
          
          <div className="p-3 border-t border-dark-text-secondary/20 text-center">
            <a href="/notifications" className="text-sm text-purple-400 hover:text-purple-300">
              View all notifications →
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

// Notifications Settings Page
const NotificationSettings = ({ Header, Footer }) => {
  const [settings, setSettings] = useState({
    new_chapter: true,
    comments: true,
    votes: true,
    achievements: true,
    contests: true,
    payments: true,
    marketing: false
  });
  const [saving, setSaving] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      const response = await apiCall('get', '/notifications/settings');
      setSettings(response.data || settings);
    } catch (error) {
      console.error('Failed to load notification settings:', error);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await apiCall('put', '/notifications/settings', settings);
      alert('Settings saved!');
    } catch (error) {
      alert('Failed to save settings');
    }
    setSaving(false);
  };

  const toggleSetting = (key) => {
    setSettings(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const settingsList = [
    { key: 'new_chapter', icon: '📖', title: 'New Chapters', description: 'When a story you follow has a new chapter' },
    { key: 'comments', icon: '💬', title: 'Comments', description: 'When someone comments on your story or replies to you' },
    { key: 'votes', icon: '❤️', title: 'Likes', description: 'When someone likes your story' },
    { key: 'achievements', icon: '🏆', title: 'Achievements', description: 'When you unlock a new badge or achievement' },
    { key: 'contests', icon: '🎯', title: 'Contests', description: 'Contest updates and announcements' },
    { key: 'payments', icon: '💰', title: 'Payments', description: 'Earnings and payment notifications' },
    { key: 'marketing', icon: '📢', title: 'Marketing', description: 'News, updates, and promotions from StoryfYC' },
  ];

  return (
    <div className="min-h-screen bg-dark-bg">
      <Header />
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <h1 className="text-3xl font-bold text-dark-text mb-8">🔔 Notification Settings</h1>
        
        <div className="bg-dark-card rounded-xl border border-dark-text-secondary/20">
          {settingsList.map((setting, i) => (
            <div
              key={setting.key}
              className={`p-4 flex items-center justify-between ${
                i < settingsList.length - 1 ? 'border-b border-dark-text-secondary/10' : ''
              }`}
            >
              <div className="flex items-center gap-4">
                <span className="text-2xl">{setting.icon}</span>
                <div>
                  <p className="text-dark-text font-medium">{setting.title}</p>
                  <p className="text-dark-text-secondary text-sm">{setting.description}</p>
                </div>
              </div>
              <button
                onClick={() => toggleSetting(setting.key)}
                className={`w-12 h-6 rounded-full transition-colors ${
                  settings[setting.key] ? 'bg-purple-500' : 'bg-dark-bg'
                }`}
                data-testid={`toggle-${setting.key}`}
              >
                <span className={`block w-5 h-5 bg-white rounded-full transition-transform mx-0.5 ${
                  settings[setting.key] ? 'translate-x-6' : 'translate-x-0'
                }`} />
              </button>
            </div>
          ))}
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={saveSettings}
            disabled={saving}
            className="bg-purple-500 text-white px-8 py-3 rounded-lg hover:bg-purple-600 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default NotificationSettings;
